import { createStore } from 'vuex';

// Import modules
import auth from './modules/auth.js';
import admin from './modules/admin.js';
import games from './modules/games.js';
import betting from './modules/betting.js';
import notifications from './modules/notifications.js';

// Create store
const store = createStore({
  state: {
    // Global app state
    appVersion: '2.0.0',
    isOnline: navigator.onLine,
    isMobile: window.innerWidth < 768,
    sidebarOpen: window.innerWidth >= 1024,
    theme: localStorage.getItem('dale365-theme') || 'dark',
  },
  
  mutations: {
    SET_ONLINE_STATUS(state, isOnline) {
      state.isOnline = isOnline;
    },
    
    SET_MOBILE_STATUS(state, isMobile) {
      state.isMobile = isMobile;
    },
    
    TOGGLE_SIDEBAR(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    
    SET_SIDEBAR(state, open) {
      state.sidebarOpen = open;
    },
    
    SET_THEME(state, theme) {
      state.theme = theme;
      localStorage.setItem('dale365-theme', theme);
      document.documentElement.setAttribute('data-theme', theme);
    },
  },
  
  actions: {
    // Initialize app
    async initializeApp({ dispatch, commit }) {
      // Set up online/offline listeners
      window.addEventListener('online', () => {
        commit('SET_ONLINE_STATUS', true);
        dispatch('notifications/showInfo', 'Conexão restaurada', { root: true });
      });
      
      window.addEventListener('offline', () => {
        commit('SET_ONLINE_STATUS', false);
        dispatch('notifications/showError', 'Sem conexão com a internet', { root: true });
      });
      
      // Set up responsive listeners
      const checkMobile = () => {
        const isMobile = window.innerWidth < 768;
        commit('SET_MOBILE_STATUS', isMobile);
        if (isMobile) {
          commit('SET_SIDEBAR', false);
        }
      };
      
      window.addEventListener('resize', checkMobile);
      checkMobile();
      
      // Initialize modules
      await dispatch('auth/checkAuth');
      dispatch('notifications/initToast');
    },
    
    // Handle IP blocking
    handleIpBlock({ dispatch }) {
      // Clear all auth data
      localStorage.setItem('ipBlocked', 'true');
      document.cookie = 'dale_blocked=1; path=/; expires=Fri, 31 Dec 2038 23:59:59 GMT';
      
      // Clear auth
      dispatch('auth/logout');
      
      // Redirect to blocked page
      window.location.replace('/blocked');
    },
    
    // Theme management
    toggleTheme({ state, commit }) {
      const newTheme = state.theme === 'dark' ? 'light' : 'dark';
      commit('SET_THEME', newTheme);
    },
    
    setTheme({ commit }, theme) {
      commit('SET_THEME', theme);
    },
    
    // Sidebar management
    toggleSidebar({ commit }) {
      commit('TOGGLE_SIDEBAR');
    },
    
    setSidebar({ commit }, open) {
      commit('SET_SIDEBAR', open);
    },
  },
  
  getters: {
    isOnline: state => state.isOnline,
    isMobile: state => state.isMobile,
    sidebarOpen: state => state.sidebarOpen,
    currentTheme: state => state.theme,
    isDarkMode: state => state.theme === 'dark',
  },
  
  modules: {
    auth,
    admin,
    games,
    betting,
    notifications,
  },
});

// Hot reload modules
if (module.hot) {
  // Accept the hot update for this module
  module.hot.accept();
  
  // Update modules
  module.hot.accept('./modules/auth.js', () => {
    const newAuth = require('./modules/auth.js').default;
    store.hotUpdate({ modules: { auth: newAuth } });
  });
  
  module.hot.accept('./modules/admin.js', () => {
    const newAdmin = require('./modules/admin.js').default;
    store.hotUpdate({ modules: { admin: newAdmin } });
  });
  
  module.hot.accept('./modules/games.js', () => {
    const newGames = require('./modules/games.js').default;
    store.hotUpdate({ modules: { games: newGames } });
  });
  
  module.hot.accept('./modules/betting.js', () => {
    const newBetting = require('./modules/betting.js').default;
    store.hotUpdate({ modules: { betting: newBetting } });
  });
  
  module.hot.accept('./modules/notifications.js', () => {
    const newNotifications = require('./modules/notifications.js').default;
    store.hotUpdate({ modules: { notifications: newNotifications } });
  });
}

export default store;